import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { List } from 'immutable'
import compose from 'recompose/compose'
import withHandlers from 'recompose/withHandlers'
import withProps from 'recompose/withProps'
import lifecycle from 'recompose/lifecycle'
import branch from 'recompose/branch'
import renderComponent from 'recompose/renderComponent'
import { contains, isEmpty } from 'ramda'
import { pickBy } from 'lodash'
import prop from 'ramda/src/prop'
import { SmartFilterValues } from '@driftt/audience-builder'

import * as PlaybookActions from 'app/modules/playbook/actions'
import * as SegmentActions from 'app/modules/segment/actions'
import * as AccountActions from 'app/modules/account/actions'
import * as OrgActions from 'app/modules/org/actions'
import * as PlaybookSelectors from 'app/modules/playbook/selectors'
import * as SegmentSelectors from 'app/modules/segment/selectors'
import * as OrgSelectors from 'app/modules/org/selectors'
import * as AccountSelectors from 'app/modules/account/selectors'
import { AudienceType } from 'app/modules/playbook/constants'

import SegmentSelect from '../SegmentSelect'
import { makeHasGate } from 'app/modules/gates/selectors'
import ACCESS from 'utils/Access'

import AudienceBuilderContainer from './AudienceBuilderContainer'

const mapStateToProps = state => ({
  audience: PlaybookSelectors.audience(state),
  segmentSelectVisible: PlaybookSelectors.segmentSelectVisible(state),
  isMessageTypePlaybook: PlaybookSelectors.isMessageTypePlaybook(state),
  isEmailPlaybook: PlaybookSelectors.getIsEmailPlaybook(state),
  orgId: OrgSelectors.orgId(state),
  endUserCount: OrgSelectors.endUserCount(state),
  isCardinalityLoading: PlaybookSelectors.cardinalityLoading(state),
  targetingCardinality: PlaybookSelectors.targetingCardinality(state),
  segments: SegmentSelectors.byId(state),
  userListIds: PlaybookSelectors.userListIds(state),
  excludedUserListIds: PlaybookSelectors.excludedUserListIds(state),
  accountListIds: PlaybookSelectors.accountListIds(state),
  excludedAccountListIds: PlaybookSelectors.excludedAccountListIds(state),
  accountSegments: AccountSelectors.getAccountSegments(state),
  smartFilters: PlaybookSelectors.builderSmartFilters(state),
  accountSegmentsIsLoading: AccountSelectors.getAccountSegmentsIsLoading(state),
  hasSmartFilterInclusion: !PlaybookSelectors.getIsEmailPlaybook(state),
  hasAccountSegmentation: makeHasGate('ABM')(state) && !PlaybookSelectors.getIsEmailPlaybook(state),
  hasAbandonedMeetingSmartFilter: ACCESS.isAtLeastProPlan(),
  hasLimitedContactsMktEmail: OrgSelectors.hasLimitedContactsMktEmail(state)
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchSegmentList: SegmentActions.fetchSegmentList,
      setAudience: PlaybookActions.setAudience,
      showSegmentSelect: PlaybookActions.showSegmentSelect,
      fetchEndUserCount: OrgActions.fetchEndUserCount,
      fetchAccountSegments: AccountActions.fetchAccountSegments,
      setAccountListIds: PlaybookActions.setAccountListIds,
      setExcludedAccountListIds: PlaybookActions.setExcludedAccountListIds,
      setUserListIds: PlaybookActions.setUserListIds,
      fetchPlaybookCardinality: PlaybookActions.fetchPlaybookCardinality,
      setExcludedUserListIds: PlaybookActions.setExcludedUserListIds,
      setSmartFilters: PlaybookActions.setSmartFilters,
      hasLimitedContactsMktEmail: OrgActions.hasLimitedContactsMktEmail
    },
    dispatch
  ),
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      // TODO: this takes `index` and `size` because it's paginated, we should figure this out later
      if (this.props.hasAccountSegmentation) {
        // don't fetch accounts unless there is accounts segmentation
        this.props.actions.fetchAccountSegments({ index: 0, size: 200 })
      }
      this.props.actions.fetchEndUserCount(this.props.orgId)

      if (this.props.isEmailPlaybook) {
        const { userListIds, excludedUserListIds, orgId, smartFilters, isEmailPlaybook } = this.props
        this.props.actions.fetchPlaybookCardinality({ orgId, userListIds, excludedUserListIds, smartFilters, isEmailPlaybook })
        this.props.actions.hasLimitedContactsMktEmail()
      }
    },
  }),
  withProps(props => ({
    everyoneSelected: props.audience === AudienceType.EVERYONE,
    segmentSelected: props.audience === AudienceType.SEGMENT,
    usersSelected: props.audience === AudienceType.USERS,
    visitorsSelected: props.audience === AudienceType.VISITORS,
    noneSelected: props.audience === AudienceType.NONE,
    onSegmentChange: (userListIds, excludedUserListIds, smartFilters) => {
      const { orgId, isEmailPlaybook } = props

      props.actions.fetchPlaybookCardinality({ orgId, userListIds, excludedUserListIds, smartFilters, isEmailPlaybook })
    },
    clearInclusiveSegments: () => {
      props.actions.setUserListIds(List([]))
      props.actions.setAccountListIds(List([]))
    },
  })),
  withHandlers({
    onEveryoneClick: props => () => {
      props.actions.setAudience(AudienceType.EVERYONE)
      props.clearInclusiveSegments()
    },
    onSegmentClick: props => () => {
      props.actions.showSegmentSelect()
    },
    onUsersClick: props => () => {
      props.actions.setAudience(AudienceType.USERS)
      props.clearInclusiveSegments()
    },
    onVisitorsClick: props => () => {
      props.actions.setAudience(AudienceType.VISITORS)
      props.clearInclusiveSegments()
    },
    onNoneClick: props => () => {
      props.actions.setAudience(AudienceType.NONE)
      props.clearInclusiveSegments()
    },
  }),
  withProps(props => ({
    loading: (props.accountSegmentsIsLoading || (props.segments.size === 0 && (!props.userListIds || !props.excludedUserListIds))),
    saveAudience: (selections) => {
      const { selectedSendContacts, selectedDontSendAccounts, selectedDontSendContacts, selectedAccounts, selectedFilters } = selections
      const includedUserListIds = List(selectedSendContacts)
      const excludedUserListIds = List(selectedDontSendContacts)
      props.actions.setSmartFilters(selectedFilters)

      const sendSegmentsAreEmpty = isEmpty(selectedSendContacts) && isEmpty(selectedAccounts) && isEmpty(pickBy(selectedFilters, (value) => value === SmartFilterValues.INCLUDE))

      // always set excluded
      props.actions.setExcludedUserListIds(excludedUserListIds)
      props.actions.setExcludedAccountListIds(List(selectedDontSendAccounts))

      // Special case these as they map to specific actions
      if (!props.isEmailPlaybook && sendSegmentsAreEmpty) {
        props.onEveryoneClick()
      } else if (contains(AudienceType.USERS, selectedSendContacts)) {
        props.onUsersClick()
      } else if (contains(AudienceType.VISITORS, selectedSendContacts)) {
        props.onVisitorsClick()
      } else {
        props.actions.setAudience(AudienceType.SEGMENT)
        props.actions.setUserListIds(includedUserListIds)
        props.actions.setAccountListIds(List(selectedAccounts))
        props.onSegmentChange(includedUserListIds, excludedUserListIds, selectedFilters)
      }
    },
  })),
  branch(prop('segmentSelectVisible'), renderComponent(SegmentSelect)),
)(AudienceBuilderContainer)



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/TargetingAudience/index.js